import { setup } from "xstate"
import type { RouteLocationNormalizedLoaded } from "vue-router"

// ================================
// TYPES
// ================================

/** Contexte vide : user, company, pipedrive sont gérés dans le store subscription-form. */
export type FormPaymentContext = Record<string, never>

export interface FormPaymentInput {
    getRoute: () => RouteLocationNormalizedLoaded
}

export type FormPaymentEvent =
    | { type: "REDIRECTION" }
    | { type: "RESTORE"; step: FormPaymentRestoreStep }
    | { type: "NEXT" }
    | { type: "PREVIOUS" }
    | { type: "SUBMIT" }
    | { type: "BACK" }
    | { type: "MODIFY" }

/** Étapes restaurables depuis un brouillon (sans Success/Failure). */
export type FormPaymentRestoreStep =
    | "FormPaymentIntro"
    | "FormPaymentOffer"
    | "FormPaymentSources"
    | "FormPaymentRecap"

/** État courant du parcours plan (offre → sources → récap → succès/échec). */
export type FormPaymentPlanStep =
    | "FormPaymentOffer"
    | "FormPaymentSources"
    | "FormPaymentRecap"
    | "FormPaymentSuccess"
    | "FormPaymentFailure"

// ================================
// MACHINE
// ================================

/**
 * Machine unique du parcours inscription : Intro → Offre → Sources → Succès/Échec.
 * - Au montage, REDIRECTION envoie vers Intro, ou Success/Failure si retour Stripe (?status=).
 * - User, company, pipedrive : gérés dans le store subscription-form.
 */
export function createFormPaymentMachine(input: FormPaymentInput) {
    return setup({
        types: {
            input: {} as FormPaymentInput,
            context: {} as FormPaymentContext,
            events: {} as FormPaymentEvent,
        },
        guards: {
            isSuccess: () => input.getRoute().query.status === "success",
            isFailure: () => input.getRoute().query.status === "failure",
            isRestoreIntro: ({ event }) =>
                event.type === "RESTORE" && event.step === "FormPaymentIntro",
            isRestoreOffer: ({ event }) =>
                event.type === "RESTORE" && event.step === "FormPaymentOffer",
            isRestoreSources: ({ event }) =>
                event.type === "RESTORE" && event.step === "FormPaymentSources",
            isRestoreRecap: ({ event }) =>
                event.type === "RESTORE" && event.step === "FormPaymentRecap",
        },
    }).createMachine({
        id: "formPayment",
        initial: "idle",
        context: {} as FormPaymentContext,
        states: {
            idle: {
                on: {
                    REDIRECTION: [
                        {
                            target: "FormPaymentSuccess",
                            guard: "isSuccess",
                        },
                        {
                            target: "FormPaymentFailure",
                            guard: "isFailure",
                        },
                        {
                            target: "FormPaymentIntro",
                        },
                    ],
                    RESTORE: [
                        {
                            target: "FormPaymentIntro",
                            guard: "isRestoreIntro",
                        },
                        {
                            target: "FormPaymentOffer",
                            guard: "isRestoreOffer",
                        },
                        {
                            target: "FormPaymentSources",
                            guard: "isRestoreSources",
                        },
                        {
                            target: "FormPaymentRecap",
                            guard: "isRestoreRecap",
                        },
                    ],
                },
            },
            FormPaymentIntro: {
                on: {
                    NEXT: { target: "FormPaymentOffer" },
                },
            },
            FormPaymentOffer: {
                on: {
                    SUBMIT: { target: "FormPaymentSources" },
                    MODIFY: { target: "FormPaymentSources" },
                    PREVIOUS: { target: "FormPaymentIntro" },
                },
            },
            FormPaymentSources: {
                on: {
                    NEXT: { target: "FormPaymentRecap" },
                    BACK: { target: "FormPaymentOffer" },
                    MODIFY: { target: "FormPaymentOffer" },
                },
            },
            FormPaymentRecap: {
                on: {
                    BACK: { target: "FormPaymentSources" },
                },
            },
            FormPaymentSuccess: {},
            FormPaymentFailure: {
                on: {
                    BACK: { target: "FormPaymentRecap" },
                },
            },
        },
    })
}
